// Prozedurale Pixel-Icons — kein einziges Bild-Asset.
//
// Alle Formen liegen in einem **16×16-Raster** und werden hochskaliert; ein
// Icon mit anderem Rastermass faellt aus dem Bild (VIEW-001).
//
// Ein unbekannter Icon-Name faellt **still auf das Schloss zurueck**: keine
// Warnung, kein Konsoleneintrag. Genau deshalb ist `ICON_KEYS` exportiert und
// Gate FG-25 vergleicht es gegen die Namen aus den Datentabellen. Beim
// Einfuehren des Gates waren drei Namen falsch — alle drei zeigten ein Schloss,
// im Technologiebaum also ausgerechnet das Zeichen fuer „verriegelt".

import { TAU } from '../core/math';
import type { Resource } from '../data/types';

interface IconDef {
  shape: string;
  color: string;
  tier?: number;
}

const DEFS: Record<string, IconDef> = {
  laser: { shape: 'beam', color: '#5fe0ff', tier: 1 },
  laser2: { shape: 'beam', color: '#5fe0ff', tier: 2 },
  laser3: { shape: 'beam', color: '#b98cff', tier: 3 },
  range: { shape: 'radar', color: '#ffd84d' },
  rate: { shape: 'bolt', color: '#ffd84d', tier: 1 },
  rate2: { shape: 'bolt', color: '#ffd84d', tier: 2 },
  targets: { shape: 'cross', color: '#ff8a5c', tier: 1 },
  targets2: { shape: 'cross', color: '#ff8a5c', tier: 2 },
  bounce: { shape: 'zigzag', color: '#9ff0ff' },
  bouncepow: { shape: 'zigzag', color: '#ffe98a', tier: 2 },
  crit: { shape: 'star', color: '#ffe98a' },
  critdmg: { shape: 'star', color: '#ff6a5c', tier: 2 },
  overdrive: { shape: 'ring', color: '#ff4de0', tier: 3 },

  missile: { shape: 'rocket', color: '#ff9a3c' },
  missiledmg: { shape: 'rocket', color: '#ff6a3c', tier: 1 },
  missilerate: { shape: 'rocket', color: '#ffd84d', tier: 2 },
  splash: { shape: 'burst', color: '#ff9a3c' },
  salvo: { shape: 'rocket', color: '#ff4d4d', tier: 3 },

  drone: { shape: 'drone', color: '#7ee0ff' },
  dronecount: { shape: 'drone', color: '#7ee0ff', tier: 2 },
  dronedmg: { shape: 'drone', color: '#ff9a3c', tier: 3 },
  // Die dritte Achse des Zweigs: Reichweite. Radar statt Drohne, weil sie
  // genau das ist, was der Ast vor dem Laser voraushat.
  dronerange: { shape: 'radar', color: '#7ee0ff', tier: 2 },

  nova: { shape: 'ring', color: '#7ee0ff' },
  novadmg: { shape: 'burst', color: '#7ee0ff', tier: 2 },
  novarate: { shape: 'ring', color: '#ffd84d', tier: 2 },

  battery: { shape: 'battery', color: '#5fe6c8', tier: 1 },
  battery2: { shape: 'battery', color: '#5fe6c8', tier: 2 },
  regen: { shape: 'bolt', color: '#5fe6c8' },
  eff: { shape: 'snow', color: '#9fe8ff' },
  thrust: { shape: 'flame', color: '#ff9a3c' },
  boost: { shape: 'flame', color: '#ffd84d', tier: 2 },

  magnet: { shape: 'magnet', color: '#e05a8a' },
  magnetspeed: { shape: 'magnet', color: '#ffd84d', tier: 2 },
  // Die Linsenkette: die Falle ist ein Magnet in Kristallviolett, die Linse
  // ein Edelstein im Blau der Fokuslinsen-Erzfarbe.
  trap: { shape: 'magnet', color: '#c07ce0', tier: 3 },
  lens: { shape: 'gem', color: '#4d9cff', tier: 2 },
  // `ore` und `radar` sind tierlose Grundvarianten von `yield`/`range`. Sie
  // werden dort benutzt, wo ein Tier-Punkt falsch aussieht.
  ore: { shape: 'ore', color: '#c3bcae' },
  radar: { shape: 'radar', color: '#ffd84d' },
  // Peilung: dieselbe Schuessel, aber in der Farbe der Energie — sie sucht
  // eine Tankstelle, keinen Brocken.
  bearing: { shape: 'radar', color: '#5fe6c8', tier: 2 },
  yield: { shape: 'ore', color: '#c3bcae', tier: 1 },
  yield2: { shape: 'ore', color: '#f5a03a', tier: 2 },
  yield3: { shape: 'ore', color: '#8ce05a', tier: 3 },
  xp: { shape: 'chip', color: '#8ce05a' },
  luck: { shape: 'clover', color: '#8ce05a' },
  gem: { shape: 'gem', color: '#c07ce0' },
  shatter: { shape: 'shards', color: '#c3bcae' },
  field: { shape: 'clover', color: '#7ea8f0', tier: 2 },
  deepscan: { shape: 'radar', color: '#c07ce0', tier: 3 },
  shield: { shape: 'shield', color: '#7ea8f0' },
  armor: { shape: 'shield', color: '#c3bcae', tier: 2 },
  // Navigation — Karte, Kurs, Sprungfeld und ihre Fortsetzung in Akt II.
  map: { shape: 'map', color: '#7ee0ff' },
  course: { shape: 'course', color: '#8ce05a' },
  warp: { shape: 'warp', color: '#b98cff' },
  relay: { shape: 'radar', color: '#5fe6c8', tier: 3 },
  crane: { shape: 'crane', color: '#ffd84d' },
  beacon: { shape: 'warp', color: '#ff9a3c', tier: 2 },
  lock: { shape: 'lock', color: '#8a8578' },

  // ── Akt III: Güter ─────────────────────────────────────────────────────────
  // Die Farbe ist die des Gutes aus der Tabelle — wiederholt, weil ein Icon
  // ohne Tabelle zeichnen können muss (es gibt den Rückfall auf das Schloss,
  // keinen auf eine Farbe). FG-25 hält die **Namen** zusammen.
  regolith: { shape: 'heap', color: '#b08a5e' },
  ironsand: { shape: 'heap', color: '#a2563e' },
  ice: { shape: 'block', color: '#b8ecff' },
  quartz: { shape: 'heap', color: '#ece2c4' },
  sulfur: { shape: 'heap', color: '#e8d23a' },
  gas: { shape: 'cloud', color: '#a4b8ff' },
  spores: { shape: 'sprout', color: '#7ce07c' },
  brick: { shape: 'brick', color: '#d0683e' },
  steel: { shape: 'ingot', color: '#a4b2c2' },
  water: { shape: 'drop', color: '#3a90ff' },
  glass: { shape: 'pane', color: '#a6f2ee' },
  acid: { shape: 'drop', color: '#c6f53a', tier: 1 },
  nitrogen: { shape: 'cloud', color: '#6a78ff', tier: 1 },
  frame: { shape: 'girder', color: '#dca04e' },
  circuit: { shape: 'chip', color: '#3cd07c' },
  soil: { shape: 'heap', color: '#7a5232', tier: 1 },
  habitat: { shape: 'dome', color: '#ececec' },
  seed: { shape: 'sprout', color: '#a8d444', tier: 2 },
  atmo: { shape: 'orb', color: '#64c4ff' },
  biome: { shape: 'clover', color: '#44ff9e', tier: 3 },
  // ── Akt III: Anlagen ───────────────────────────────────────────────────────
  pod: { shape: 'pod', color: '#e8e2d4' },
  drill: { shape: 'drill', color: '#ffd84d' },
  solar: { shape: 'panel', color: '#4d9cff' },
  kiln: { shape: 'furnace', color: '#ff9a3c' },
  smelter: { shape: 'factory', color: '#a4b2c2' },
  works: { shape: 'factory', color: '#dca04e', tier: 1 },
  geo: { shape: 'tower', color: '#ff6a3c' },
  still: { shape: 'tower', color: '#3a90ff', tier: 1 },
  chem: { shape: 'flask', color: '#c6f53a' },
  fab: { shape: 'factory', color: '#3cd07c', tier: 2 },
  fusion: { shape: 'reactor', color: '#ff4de0' },
  atmoplant: { shape: 'factory', color: '#64c4ff', tier: 3 },
  greenhouse: { shape: 'dome', color: '#a8d444', tier: 2 },
  biolab: { shape: 'flask', color: '#44ff9e', tier: 3 },
  planet: { shape: 'planet', color: '#9cff8a' },
};

/**
 * Alle bekannten Icon-Schluessel.
 *
 * Datentabellen duerfen nur diese Namen nennen — `drawIcon` faellt sonst still
 * auf das Schloss zurueck (Gate FG-25).
 */
export const ICON_KEYS: readonly string[] = Object.keys(DEFS);

/** Das Rastermass. Jede Form rechnet in diesem Koordinatensystem. */
export const ICON_GRID = 16;

type Ctx = CanvasRenderingContext2D;

const shade = (hex: string, factor: number): string => {
  const n = parseInt(hex.slice(1), 16);
  const c = (v: number): number => Math.max(0, Math.min(255, Math.round(v * factor)));
  return `rgb(${c((n >> 16) & 255)},${c((n >> 8) & 255)},${c(n & 255)})`;
};

const px = (ctx: Ctx, color: string, x: number, y: number, w = 1, h = 1): void => {
  ctx.fillStyle = color;
  ctx.fillRect(x, y, w, h);
};

const SHAPES: Record<string, (ctx: Ctx, color: string) => void> = {
  beam(ctx, c) {
    px(ctx, '#3a3f46', 2, 6, 6, 4);
    px(ctx, '#6b727c', 2, 6, 6, 1);
    px(ctx, shade(c, 1.4), 8, 7, 6, 2);
    px(ctx, c, 8, 7, 3, 2);
    px(ctx, '#fff', 13, 7, 1, 2);
  },
  radar(ctx, c) {
    ctx.strokeStyle = c; ctx.lineWidth = 1.6;
    for (let i = 1; i <= 3; i++) {
      ctx.beginPath();
      ctx.arc(4, 12, i * 3.4, -Math.PI / 2.2, 0);
      ctx.stroke();
    }
    px(ctx, '#fff', 3, 11, 2, 2);
  },
  bolt(ctx, c) {
    ctx.fillStyle = c;
    ctx.beginPath();
    ctx.moveTo(9, 1); ctx.lineTo(4, 9); ctx.lineTo(7.5, 9); ctx.lineTo(6, 15);
    ctx.lineTo(12, 6.5); ctx.lineTo(8.5, 6.5); ctx.closePath();
    ctx.fill();
    px(ctx, 'rgba(255,255,255,.55)', 7, 3, 1, 4);
  },
  zigzag(ctx, c) {
    ctx.strokeStyle = c; ctx.lineWidth = 1.6;
    ctx.beginPath();
    ctx.moveTo(1.5, 12); ctx.lineTo(6, 4); ctx.lineTo(9.5, 11); ctx.lineTo(14.5, 3);
    ctx.stroke();
    for (const [x, y] of [[6, 4], [9.5, 11], [14.5, 3]]) {
      ctx.fillStyle = '#4c4740';
      ctx.beginPath(); ctx.arc(x, y, 2.2, 0, TAU); ctx.fill();
      ctx.strokeStyle = '#cdc6b8'; ctx.lineWidth = 0.8; ctx.stroke();
    }
  },
  cross(ctx, c) {
    ctx.strokeStyle = c; ctx.lineWidth = 1.4;
    ctx.beginPath(); ctx.arc(8, 8, 4.4, 0, TAU); ctx.stroke();
    px(ctx, c, 7.4, 1, 1.2, 3); px(ctx, c, 7.4, 12, 1.2, 3);
    px(ctx, c, 1, 7.4, 3, 1.2); px(ctx, c, 12, 7.4, 3, 1.2);
    px(ctx, '#fff', 7, 7, 2, 2);
  },
  star(ctx, c) {
    ctx.fillStyle = c;
    ctx.beginPath();
    for (let i = 0; i < 10; i++) {
      const a = (i / 10) * TAU - Math.PI / 2;
      const r = i % 2 ? 2.6 : 6.4;
      const fn = i ? 'lineTo' : 'moveTo';
      ctx[fn](8 + Math.cos(a) * r, 8 + Math.sin(a) * r);
    }
    ctx.closePath(); ctx.fill();
    px(ctx, 'rgba(255,255,255,.7)', 7, 5, 2, 2);
  },
  ring(ctx, c) {
    ctx.strokeStyle = shade(c, 0.6); ctx.lineWidth = 2.4;
    ctx.beginPath(); ctx.arc(8, 8, 6, 0, TAU); ctx.stroke();
    ctx.strokeStyle = c; ctx.lineWidth = 1.4;
    ctx.beginPath(); ctx.arc(8, 8, 6, 0, TAU); ctx.stroke();
    px(ctx, '#fff', 7, 7, 2, 2);
  },
  rocket(ctx, c) {
    ctx.save(); ctx.translate(8, 8); ctx.rotate(-Math.PI / 4); ctx.translate(-8, -8);
    px(ctx, shade(c, 0.6), 6, 3, 4, 8);
    px(ctx, c, 6, 3, 2, 8);
    ctx.fillStyle = '#e8e2d4';
    ctx.beginPath(); ctx.moveTo(6, 3); ctx.lineTo(8, 0); ctx.lineTo(10, 3); ctx.closePath(); ctx.fill();
    px(ctx, '#4a4f57', 4, 8, 2, 3); px(ctx, '#4a4f57', 10, 8, 2, 3);
    px(ctx, '#ffdd66', 6.5, 11, 3, 3);
    ctx.restore();
  },
  burst(ctx, c) {
    ctx.fillStyle = c;
    ctx.beginPath();
    for (let i = 0; i < 12; i++) {
      const a = (i / 12) * TAU;
      const r = i % 2 ? 3 : 7;
      const fn = i ? 'lineTo' : 'moveTo';
      ctx[fn](8 + Math.cos(a) * r, 8 + Math.sin(a) * r);
    }
    ctx.closePath(); ctx.fill();
    ctx.fillStyle = '#fff6c4';
    ctx.beginPath(); ctx.arc(8, 8, 2.4, 0, TAU); ctx.fill();
  },
  drone(ctx, c) {
    px(ctx, '#3a3f46', 5, 6, 6, 4);
    px(ctx, c, 6, 7, 4, 2);
    px(ctx, '#6b727c', 2, 5, 3, 1); px(ctx, '#6b727c', 11, 5, 3, 1);
    px(ctx, '#6b727c', 2, 10, 3, 1); px(ctx, '#6b727c', 11, 10, 3, 1);
    px(ctx, shade(c, 1.6), 3, 4, 1, 1); px(ctx, shade(c, 1.6), 12, 4, 1, 1);
  },
  battery(ctx, c) {
    px(ctx, '#2b3038', 3, 3, 10, 11);
    px(ctx, '#6b727c', 3, 3, 10, 1);
    px(ctx, '#6b727c', 6, 1, 4, 2);
    px(ctx, c, 5, 5, 6, 7);
    px(ctx, shade(c, 1.5), 5, 5, 2, 7);
  },
  snow(ctx, c) {
    ctx.strokeStyle = c; ctx.lineWidth = 1.3;
    for (let i = 0; i < 3; i++) {
      const a = (i / 3) * Math.PI;
      ctx.beginPath();
      ctx.moveTo(8 - Math.cos(a) * 6, 8 - Math.sin(a) * 6);
      ctx.lineTo(8 + Math.cos(a) * 6, 8 + Math.sin(a) * 6);
      ctx.stroke();
    }
    px(ctx, '#fff', 7, 7, 2, 2);
  },
  flame(ctx, c) {
    ctx.fillStyle = shade(c, 0.8);
    ctx.beginPath(); ctx.moveTo(8, 1); ctx.quadraticCurveTo(14, 8, 8, 15);
    ctx.quadraticCurveTo(2, 8, 8, 1); ctx.fill();
    ctx.fillStyle = '#ffe98a';
    ctx.beginPath(); ctx.moveTo(8, 5); ctx.quadraticCurveTo(11, 9, 8, 13);
    ctx.quadraticCurveTo(5, 9, 8, 5); ctx.fill();
  },
  magnet(ctx, c) {
    ctx.strokeStyle = '#8a8578'; ctx.lineWidth = 3.2;
    ctx.beginPath(); ctx.arc(8, 9, 4.6, Math.PI, 0); ctx.stroke();
    px(ctx, c, 1.8, 9, 3.2, 4);
    px(ctx, '#e8e2d4', 11, 9, 3.2, 4);
  },
  ore(ctx, c) {
    ctx.fillStyle = '#4c4740';
    ctx.beginPath();
    ctx.moveTo(3, 9); ctx.lineTo(5, 4); ctx.lineTo(10, 3); ctx.lineTo(13, 7);
    ctx.lineTo(12, 12); ctx.lineTo(6, 13); ctx.closePath(); ctx.fill();
    ctx.strokeStyle = '#8f887c'; ctx.lineWidth = 1; ctx.stroke();
    px(ctx, c, 6, 6, 2, 2); px(ctx, c, 9.5, 8.5, 2, 2); px(ctx, c, 7.5, 10, 1.5, 1.5);
  },
  chip(ctx, c) {
    px(ctx, '#2b3038', 4, 4, 8, 8);
    px(ctx, c, 6, 6, 4, 4);
    for (let i = 0; i < 3; i++) {
      px(ctx, '#8f887c', 2, 5 + i * 3, 2, 1);
      px(ctx, '#8f887c', 12, 5 + i * 3, 2, 1);
      px(ctx, '#8f887c', 5 + i * 3, 2, 1, 2);
      px(ctx, '#8f887c', 5 + i * 3, 12, 1, 2);
    }
  },
  /** Karte: ein gefaltetes Blatt mit einer Markierung darauf. */
  map(ctx, c) {
    px(ctx, '#3a3f46', 2, 3, 12, 10);
    px(ctx, '#cdc6b8', 3, 4, 10, 8);
    px(ctx, '#8e8778', 6, 4, 1, 8);
    px(ctx, '#8e8778', 10, 4, 1, 8);
    ctx.fillStyle = c;
    ctx.beginPath(); ctx.arc(8.5, 7.5, 2.2, 0, TAU); ctx.fill();
    px(ctx, '#fff', 8, 7, 1, 1);
  },
  /** Kurs: eine Nadel mit Ring — der Autopilot haelt eine Richtung. */
  course(ctx, c) {
    ctx.strokeStyle = '#6b727c'; ctx.lineWidth = 1.4;
    ctx.beginPath(); ctx.arc(8, 8, 6, 0, TAU); ctx.stroke();
    ctx.fillStyle = c;
    ctx.beginPath();
    ctx.moveTo(8, 2.5); ctx.lineTo(11, 11); ctx.lineTo(8, 9); ctx.closePath();
    ctx.fill();
    ctx.fillStyle = '#cdc6b8';
    ctx.beginPath();
    ctx.moveTo(8, 13.5); ctx.lineTo(5, 5); ctx.lineTo(8, 7); ctx.closePath();
    ctx.fill();
  },
  /** Sprungfeld: gestreckte Sterne — der Raum vor dem Bug wird kuerzer. */
  warp(ctx, c) {
    ctx.strokeStyle = shade(c, 0.7); ctx.lineWidth = 1.6;
    for (const [y, len] of [[3.5, 5], [8, 9], [12.5, 5]] as const) {
      ctx.beginPath();
      ctx.moveTo(1.5, y); ctx.lineTo(1.5 + len, y);
      ctx.stroke();
    }
    ctx.fillStyle = c;
    ctx.beginPath();
    ctx.moveTo(14.5, 8); ctx.lineTo(9.5, 4); ctx.lineTo(11, 8); ctx.lineTo(9.5, 12);
    ctx.closePath(); ctx.fill();
    px(ctx, '#fff', 12, 7.4, 2, 1.2);
  },
  /** Kran: Mast, Ausleger und ein haengender Haken. */
  crane(ctx, c) {
    px(ctx, '#6b727c', 2, 12, 6, 2);
    px(ctx, '#8e8778', 4, 3, 2, 10);
    px(ctx, c, 4, 2.5, 9, 1.6);
    ctx.strokeStyle = '#6b727c'; ctx.lineWidth = 1;
    ctx.beginPath(); ctx.moveTo(11.5, 4); ctx.lineTo(11.5, 8); ctx.stroke();
    px(ctx, '#cdc6b8', 9.5, 8, 4, 4);
    px(ctx, '#3a3f46', 10.5, 9, 2, 2);
  },
  clover(ctx, c) {
    ctx.fillStyle = c;
    for (const [dx, dy] of [[-3, -3], [3, -3], [-3, 3], [3, 3]]) {
      ctx.beginPath(); ctx.arc(8 + dx, 8 + dy, 3.2, 0, TAU); ctx.fill();
    }
    px(ctx, shade(c, 0.5), 7.5, 8, 1, 6);
  },
  gem(ctx, c) {
    ctx.fillStyle = c;
    ctx.beginPath();
    ctx.moveTo(8, 1.5); ctx.lineTo(14, 7); ctx.lineTo(8, 14.5); ctx.lineTo(2, 7);
    ctx.closePath(); ctx.fill();
    ctx.fillStyle = 'rgba(255,255,255,.55)';
    ctx.beginPath(); ctx.moveTo(8, 1.5); ctx.lineTo(11, 7); ctx.lineTo(8, 7); ctx.closePath(); ctx.fill();
    ctx.strokeStyle = shade(c, 0.5); ctx.lineWidth = 1;
    ctx.beginPath(); ctx.moveTo(2, 7); ctx.lineTo(14, 7); ctx.stroke();
  },
  shards(ctx, c) {
    ctx.fillStyle = c;
    const tri = (x: number, y: number, s: number): void => {
      ctx.beginPath(); ctx.moveTo(x, y); ctx.lineTo(x + s, y + s * 0.6);
      ctx.lineTo(x + s * 0.3, y + s); ctx.closePath(); ctx.fill();
    };
    tri(2, 2, 5); tri(9, 4, 5); tri(5, 9, 5);
  },
  shield(ctx, color) {
    // Ein Wappenschild: oben gerade, unten spitz. Bleibt im 16x16-Raster.
    ctx.fillStyle = shade(color, 0.55);
    ctx.beginPath();
    ctx.moveTo(3, 2.5); ctx.lineTo(13, 2.5); ctx.lineTo(13, 8);
    ctx.lineTo(8, 13.5); ctx.lineTo(3, 8); ctx.closePath();
    ctx.fill();
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.moveTo(4.5, 4); ctx.lineTo(11.5, 4); ctx.lineTo(11.5, 7.6);
    ctx.lineTo(8, 11.6); ctx.lineTo(4.5, 7.6); ctx.closePath();
    ctx.fill();
    px(ctx, shade(color, 1.5), 7.2, 5, 1.6, 4);
  },
  // ── Akt III ────────────────────────────────────────────────────────────────
  /** Ein Haufen Körner — Rohstoffe, die man schaufelt. */
  heap(ctx, c) {
    ctx.fillStyle = shade(c, 0.55);
    ctx.beginPath(); ctx.moveTo(1.5, 13.5); ctx.lineTo(8, 4); ctx.lineTo(14.5, 13.5); ctx.closePath(); ctx.fill();
    ctx.fillStyle = c;
    ctx.beginPath(); ctx.moveTo(3.5, 13.5); ctx.lineTo(8, 6.5); ctx.lineTo(12.5, 13.5); ctx.closePath(); ctx.fill();
    for (const [x, y] of [[6, 10], [9, 9], [7.5, 12], [10.5, 12]]) px(ctx, shade(c, 1.4), x, y, 1, 1);
  },
  /** Ein Block mit Kante — Eis. */
  block(ctx, c) {
    px(ctx, shade(c, 0.6), 3, 5, 10, 9);
    px(ctx, c, 3, 5, 9, 8);
    px(ctx, shade(c, 1.3), 4, 3, 10, 2);
    px(ctx, 'rgba(255,255,255,.7)', 4, 6, 2, 5);
  },
  /** Zwei versetzte Ziegel. */
  brick(ctx, c) {
    px(ctx, shade(c, 0.5), 1.5, 4.5, 13, 9);
    px(ctx, c, 2, 5, 6, 3.5); px(ctx, c, 8.5, 5, 5.5, 3.5);
    px(ctx, c, 2, 9, 3.5, 4); px(ctx, c, 6, 9, 8, 4);
  },
  /** Ein Barren im Profil. */
  ingot(ctx, c) {
    ctx.fillStyle = shade(c, 0.55);
    ctx.beginPath(); ctx.moveTo(1.5, 12); ctx.lineTo(4, 6);
    ctx.lineTo(12, 6); ctx.lineTo(14.5, 12); ctx.closePath(); ctx.fill();
    ctx.fillStyle = c;
    ctx.beginPath(); ctx.moveTo(4, 6); ctx.lineTo(5.5, 3.5);
    ctx.lineTo(10.5, 3.5); ctx.lineTo(12, 6); ctx.closePath(); ctx.fill();
    px(ctx, 'rgba(255,255,255,.6)', 6, 4.2, 4, 1);
  },
  /** Ein Tropfen. */
  drop(ctx, c) {
    ctx.fillStyle = c;
    ctx.beginPath(); ctx.moveTo(8, 1.5);
    ctx.quadraticCurveTo(13.5, 9, 8, 14.5); ctx.quadraticCurveTo(2.5, 9, 8, 1.5); ctx.fill();
    px(ctx, 'rgba(255,255,255,.65)', 6, 8, 1.5, 3);
  },
  /** Eine Scheibe Glas mit Glanz. */
  pane(ctx, c) {
    px(ctx, shade(c, 0.5), 3, 2, 10, 12);
    px(ctx, c, 4, 3, 8, 10);
    ctx.strokeStyle = 'rgba(255,255,255,.8)'; ctx.lineWidth = 1;
    ctx.beginPath(); ctx.moveTo(5, 10); ctx.lineTo(9, 4); ctx.stroke();
  },
  /** Eine Wolke. */
  cloud(ctx, c) {
    ctx.fillStyle = c;
    for (const [x, y, r] of [[5, 9, 3.2], [8.5, 7, 4], [11.5, 9.5, 3]] as const) {
      ctx.beginPath(); ctx.arc(x, y, r, 0, TAU); ctx.fill();
    }
    px(ctx, c, 3, 9.5, 10, 3);
    px(ctx, 'rgba(255,255,255,.55)', 7, 4.5, 3, 1);
  },
  /** Ein Keimling. */
  sprout(ctx, c) {
    px(ctx, '#6b4a2a', 3, 12, 10, 2.5);
    px(ctx, shade(c, 0.6), 7.5, 6, 1.2, 6);
    ctx.fillStyle = c;
    ctx.beginPath(); ctx.ellipse(5.5, 6, 3, 1.8, -0.5, 0, TAU); ctx.fill();
    ctx.beginPath(); ctx.ellipse(10.5, 4.5, 3, 1.8, 0.5, 0, TAU); ctx.fill();
  },
  /** Ein Fachwerkträger. */
  girder(ctx, c) {
    px(ctx, c, 1, 3, 14, 2); px(ctx, c, 1, 11, 14, 2);
    ctx.strokeStyle = shade(c, 0.7); ctx.lineWidth = 1.4;
    ctx.beginPath();
    ctx.moveTo(2, 11); ctx.lineTo(5, 5); ctx.lineTo(8, 11); ctx.lineTo(11, 5); ctx.lineTo(14, 11);
    ctx.stroke();
  },
  /** Eine Kuppel auf einem Sockel. */
  dome(ctx, c) {
    px(ctx, '#6b727c', 1.5, 11.5, 13, 2.5);
    ctx.fillStyle = shade(c, 0.6);
    ctx.beginPath(); ctx.arc(8, 11.5, 6, Math.PI, 0); ctx.fill();
    ctx.fillStyle = c;
    ctx.beginPath(); ctx.arc(8, 11.5, 4.6, Math.PI, 0); ctx.fill();
    px(ctx, 'rgba(255,255,255,.7)', 5.5, 8, 1.5, 2);
  },
  /** Eine leuchtende Kugel mit Ring. */
  orb(ctx, c) {
    ctx.fillStyle = shade(c, 0.6);
    ctx.beginPath(); ctx.arc(8, 8, 5.5, 0, TAU); ctx.fill();
    ctx.fillStyle = c;
    ctx.beginPath(); ctx.arc(7.2, 7.2, 3.6, 0, TAU); ctx.fill();
    ctx.strokeStyle = '#e8e2d4'; ctx.lineWidth = 1;
    ctx.beginPath(); ctx.ellipse(8, 8, 7.2, 2.2, -0.35, 0, TAU); ctx.stroke();
  },
  /** Die Landekapsel: Rumpf auf drei Beinen. */
  pod(ctx, c) {
    px(ctx, '#6b727c', 2.5, 12, 2, 3); px(ctx, '#6b727c', 11.5, 12, 2, 3); px(ctx, '#6b727c', 7, 12, 2, 3);
    ctx.fillStyle = shade(c, 0.7);
    ctx.beginPath(); ctx.moveTo(3, 12.5); ctx.lineTo(5, 3.5);
    ctx.lineTo(11, 3.5); ctx.lineTo(13, 12.5); ctx.closePath(); ctx.fill();
    px(ctx, '#5fe6c8', 6.5, 6, 3, 2.5);
  },
  /** Ein Förderturm mit Bohrkopf. */
  drill(ctx, c) {
    ctx.strokeStyle = '#8e8778'; ctx.lineWidth = 1.4;
    ctx.beginPath(); ctx.moveTo(3, 13); ctx.lineTo(8, 1.5); ctx.lineTo(13, 13); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(5, 8.5); ctx.lineTo(11, 8.5); ctx.stroke();
    ctx.fillStyle = c;
    ctx.beginPath(); ctx.moveTo(6, 10); ctx.lineTo(10, 10); ctx.lineTo(8, 15); ctx.closePath(); ctx.fill();
  },
  /** Ein Solarfeld in Schrägsicht. */
  panel(ctx, c) {
    ctx.fillStyle = shade(c, 0.6);
    ctx.beginPath(); ctx.moveTo(1.5, 12); ctx.lineTo(5, 3);
    ctx.lineTo(14.5, 3); ctx.lineTo(11, 12); ctx.closePath(); ctx.fill();
    ctx.strokeStyle = c; ctx.lineWidth = 0.9;
    ctx.beginPath();
    ctx.moveTo(3.2, 7.5); ctx.lineTo(12.8, 7.5);
    ctx.moveTo(8.5, 3); ctx.lineTo(5, 12);
    ctx.stroke();
    px(ctx, '#6b727c', 7, 12, 2, 3);
  },
  /** Ein Brennofen mit Glut. */
  furnace(ctx, c) {
    px(ctx, '#5a5548', 2.5, 5, 11, 9.5);
    px(ctx, '#8e8778', 2.5, 5, 11, 1.2);
    px(ctx, '#3a3f46', 9.5, 1.5, 2.5, 3.5);
    px(ctx, '#1e1b18', 5, 8.5, 6, 4.5);
    px(ctx, c, 5.8, 10, 4.4, 3);
    px(ctx, '#ffe98a', 7, 11, 2, 1.5);
  },
  /** Eine Halle mit Sägezahndach. */
  factory(ctx, c) {
    px(ctx, '#4c4740', 1.5, 7, 13, 7.5);
    ctx.fillStyle = shade(c, 0.85);
    ctx.beginPath();
    ctx.moveTo(1.5, 7); ctx.lineTo(5.8, 3.5); ctx.lineTo(5.8, 7); ctx.lineTo(10.1, 3.5); ctx.lineTo(10.1, 7);
    ctx.lineTo(14.5, 3.5); ctx.lineTo(14.5, 7); ctx.closePath(); ctx.fill();
    px(ctx, c, 3.5, 9.5, 2.5, 2); px(ctx, c, 7, 9.5, 2.5, 2); px(ctx, c, 10.5, 9.5, 2.5, 2);
  },
  /** Ein Turm mit Kuppel — Destille und Geothermie. */
  tower(ctx, c) {
    px(ctx, '#5a5548', 5.5, 5, 5, 9.5);
    ctx.fillStyle = c;
    ctx.beginPath(); ctx.arc(8, 5, 3.4, Math.PI, 0); ctx.fill();
    px(ctx, shade(c, 1.3), 7, 7, 2, 6);
    px(ctx, '#6b727c', 3, 13, 10, 1.5);
  },
  /** Ein Kolben. */
  flask(ctx, c) {
    px(ctx, '#cdc6b8', 6.5, 1.5, 3, 4.5);
    ctx.fillStyle = '#cdc6b8';
    ctx.beginPath(); ctx.moveTo(6.5, 6); ctx.lineTo(2.5, 14);
    ctx.lineTo(13.5, 14); ctx.lineTo(9.5, 6); ctx.closePath(); ctx.fill();
    ctx.fillStyle = c;
    ctx.beginPath(); ctx.moveTo(5.2, 9); ctx.lineTo(3.4, 13);
    ctx.lineTo(12.6, 13); ctx.lineTo(10.8, 9); ctx.closePath(); ctx.fill();
  },
  /** Ein Reaktor: Ring um einen leuchtenden Kern. */
  reactor(ctx, c) {
    ctx.fillStyle = '#3a3f46';
    ctx.beginPath(); ctx.arc(8, 8, 6.5, 0, TAU); ctx.fill();
    ctx.strokeStyle = shade(c, 0.7); ctx.lineWidth = 1.6;
    ctx.beginPath(); ctx.arc(8, 8, 4.6, 0, TAU); ctx.stroke();
    ctx.fillStyle = c;
    ctx.beginPath(); ctx.arc(8, 8, 2.4, 0, TAU); ctx.fill();
    px(ctx, '#fff', 7.4, 7.4, 1.2, 1.2);
  },
  /** Ein Planet mit Licht und Schatten. */
  planet(ctx, c) {
    ctx.fillStyle = shade(c, 0.4);
    ctx.beginPath(); ctx.arc(8, 8, 6.5, 0, TAU); ctx.fill();
    ctx.fillStyle = c;
    ctx.beginPath(); ctx.arc(6.8, 6.8, 5, 0, TAU); ctx.fill();
    px(ctx, 'rgba(255,255,255,.55)', 4.5, 4.5, 2, 1.5);
    px(ctx, shade(c, 0.6), 8, 8, 2.5, 1.5);
  },
  lock(ctx) {
    ctx.strokeStyle = '#a8a294'; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.arc(8, 7, 3.2, Math.PI, 0); ctx.stroke();
    px(ctx, '#c9c2b2', 3.5, 7.5, 9, 7);
    px(ctx, '#5a5548', 7.2, 9.5, 1.6, 3);
  },
};

function drawTier(ctx: Ctx, tier: number, color: string): void {
  ctx.fillStyle = '#000';
  for (let i = 0; i < tier; i++) ctx.fillRect(2 + i * 3, 13, 2, 2);
  ctx.fillStyle = color;
  for (let i = 0; i < tier; i++) ctx.fillRect(2 + i * 3, 12, 2, 2);
}

/** Ein Icon in ein Quadrat zeichnen (x, y = linke obere Ecke, s = Kantenlaenge). */
export function drawIcon(ctx: Ctx, key: string, x: number, y: number, s: number): void {
  const def = DEFS[key] ?? DEFS.lock;
  const unit = s / ICON_GRID;
  ctx.save();
  ctx.translate(x, y);
  ctx.scale(unit, unit);
  ctx.lineCap = 'butt';
  ctx.lineJoin = 'miter';
  SHAPES[def.shape](ctx, def.color);
  if (def.tier) drawTier(ctx, def.tier, def.color);
  ctx.restore();
}

/** SVG-Markup fuer Ressourcen-Icons im DOM (Barren bzw. Kristall). */
export function resIconSvg(res: Resource, size = 16): string {
  const c = res.color;
  const d = res.dark;
  if (res.gem) {
    return `<svg class="icon" width="${size}" height="${size}" viewBox="0 0 16 16">
      <path d="M8 1 L14.5 6.5 L8 15 L1.5 6.5 Z" fill="${c}" stroke="#000" stroke-width="1.2"/>
      <path d="M8 1 L11 6.5 L8 6.5 Z" fill="#ffffff88"/>
      <path d="M1.5 6.5 L14.5 6.5" stroke="${d}" stroke-width="1"/>
    </svg>`;
  }
  return `<svg class="icon" width="${size}" height="${size}" viewBox="0 0 16 16">
    <path d="M2 10 L6 5 L15 5 L11 10 Z" fill="${c}" stroke="#000" stroke-width="1.2" stroke-linejoin="round"/>
    <path d="M2 10 L6 5 L8 5 L4 10 Z" fill="#ffffff66"/>
    <path d="M2 10 L11 10 L11 12 L2 12 Z" fill="${d}" stroke="#000" stroke-width="1.2" stroke-linejoin="round"/>
  </svg>`;
}
